import { FC, memo } from 'react';
import { Outlet } from 'react-router-dom';
import { useUser } from '@hooks/api/useUser';
import { useBalance } from '@hooks/api/useBalance';
import { useBilling } from '@hooks/common/useBilling';
import { useLogout } from '@hooks/common/useLogout';
import { usePricingContext } from '@pages/Pricing/context';
import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';
import AppLayoutV2 from '@components/theme-v2/layouts/AppLayoutV2';
import { ThemeSwitcher } from '@components/theme-v2/components/ThemeSwitcher';
import { StatusIndicator } from '@components/theme-v2/components/Card';
import AddTokensButton from '@components/AddTokensButton/AddTokensButton';
import ProfileChip from '@components/ProfileChip/ProfileChip';
import styled from 'styled-components';

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: var(--sk-bg-primary);
  color: var(--sk-text-primary);
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-lg);
`;

const HeaderActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: var(--sk-space-2);
  }
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: var(--sk-space-2) var(--sk-space-3);
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-sm);
  color: var(--sk-cyan-400);
`;

const Dashboard: FC = () => {
  const { data: userData, isLoading: userLoading } = useUser();
  const { data: balanceData } = useBalance();
  const { period } = usePricingContext();
  const { config, isLoadingMutation, isLoadingQueries } = useBilling(period);
  const { logout } = useLogout();
  const { isLargerScreen } = useScreenSizeContext();

  if (userLoading) {
    return (
      <LoadingWrapper>
        Loading...
      </LoadingWrapper>
    );
  }

  const headerActions = (
    <HeaderActionsWrapper>
      <ThemeSwitcher />
      <StatusIndicator status="active" text="Online" />
      <AddTokensButton
        tokensAmount={balanceData?.token}
        config={config}
        isDisabledAction={isLoadingMutation}
        isLoading={isLoadingQueries}
        isLargerScreen={isLargerScreen}
      />
      <ProfileChip userData={userData} handleLogout={logout} isLargerScreen={isLargerScreen} />
    </HeaderActionsWrapper>
  );

  return (
    <AppLayoutV2 headerActions={headerActions}>
      <Outlet />
    </AppLayoutV2>
  );
};

export default memo(Dashboard);
